import { escapeHtml, initials } from '../ui.js';
import { dayKey, isOpen, isLate, stateLine, parseAdd, refFor, uiState, workingOnNow, dowDate } from '../tasks-v2.js';
import { addHint } from './tasks-page.js';

// STANDUP MODE — v4.0.0 (design export 22/10/2026, p09: "In standup mode, the
// list goes full screen and you can add tasks for anyone as you talk").
//
// The morning meeting off one screen: one person at a time, their open work,
// and an add line that files a task to THEM. ←/→ (or the buttons) move between
// people, Esc closes. It reads the page's own lists and writes only through
// the page's createTask — nothing here is a new kind of record.
//
// Pure-ish: standupHtml() draws, openStandup() owns the overlay and the keys.

const e = escapeHtml;
const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** One person's page of the standup. */
export function standupHtml({ people = [], idx = 0, tasks = [], projects = {}, backOn = {}, today = dayKey() } = {}) {
  const person = people[idx];
  if (!person) return '<p class="tk4-empty">Nobody on the team yet.</p>';
  const open = tasks.filter(isOpen);
  const mine = open.filter((t) => t.assigneeId === person.userId)
    .sort((a, b) => Number(isLate(b, today)) - Number(isLate(a, today))
      || String(a.dueDate || '9999').localeCompare(String(b.dueDate || '9999')));
  const overdue = mine.filter((t) => uiState(t) === 'open' && isLate(t, today)).length;
  const now = workingOnNow(open, person.userId);
  const away = person.statusToday === 'annual_leave' || person.statusToday === 'bank_holiday';
  const meta = [plural(mine.length, 'open task'), overdue ? `${overdue} overdue` : '',
    away ? (backOn[person.userId] ? `on leave until ${dowDate(backOn[person.userId])}` : 'on leave')
      : now ? `working on ${now.title}` : ''].filter(Boolean).join(' · ');
  const strip = people.map((p, i) => {
    const n = open.filter((t) => t.assigneeId === p.userId).length;
    return `<button type="button" class="tk4-su-who${i === idx ? ' on' : ''}" data-su-person="${i}" aria-pressed="${i === idx}"
      title="${e(p.name)}"><span class="av${i === idx ? ' me' : ''}">${e(p.initials || initials(p.name))}</span><span class="tk4-su-n">${n}</span></button>`;
  }).join('');
  const rows = mine.map((t) => {
    const s = stateLine(t, people, today);
    const r = refFor(t, projects[t.projectId] || null);
    return `<div class="tk4-su-row">
      <span class="tk4-title">${e(t.title)}</span>
      <span class="tk4-ref">${r ? `<span class="tk4-num">${e(r.ref)}</span> <span class="tk4-client">${e(r.client)}</span>` : '<span class="tk4-none">No project</span>'}</span>
      <span class="tk4-state tk4-tone-${e(s.tone)}">${e(s.text)}</span>
      <span class="tk4-when${isLate(t, today) ? ' late' : ''}">${e(t.dueDate ? dowDate(t.dueDate) : '–')}</span>
    </div>`;
  }).join('');
  return `<div class="tk4-su-top">
      <div class="page-h"><h1>Standup</h1><span class="page-sub">${e(`${idx + 1} of ${people.length}`)}</span></div>
      <div class="tk4-su-strip" role="group" aria-label="People">${strip}</div>
      <div class="tk4-su-nav">
        <button type="button" class="btn icon" data-su-prev aria-label="Previous person"${idx === 0 ? ' disabled' : ''}>‹</button>
        <button type="button" class="btn icon" data-su-next aria-label="Next person"${idx >= people.length - 1 ? ' disabled' : ''}>›</button>
        <button type="button" class="btn" data-su-close>Close</button>
      </div>
    </div>
    <div class="tk4-su-person">
      <span class="av tk4-su-av">${e(person.initials || initials(person.name))}</span>
      <div><h2 class="section-h">${e(person.name)}</h2><p class="tk4-su-meta">${e([person.role, meta].filter(Boolean).join(' · '))}</p></div>
    </div>
    <label class="tk4-add tk4-su-add">
      <span class="tk4-add-plus" aria-hidden="true">+</span>
      <input class="tk4-add-in" data-su-add type="text" autocomplete="off" maxlength="300"
        placeholder="${e(`Add a task for ${String(person.name).split(/\s+/)[0]} — start with a project number to file it`)}" aria-label="${e(`Add a task for ${person.name}`)}">
      <span class="tk4-add-hint" aria-live="polite"></span>
      <kbd class="tk4-kbd">Enter</kbd>
    </label>
    <p class="tk4-su-err" role="alert" hidden></p>
    <div class="tk4-su-list">${rows || '<p class="tk4-empty">Nothing open.</p>'}</div>
    <p class="tk4-help">← and → move between people · Esc closes</p>`;
}

/**
 * @param o.people      the team
 * @param o.tasks       () => the page's current tasks (re-read after every add)
 * @param o.onCreate    async (fields) => void — the page's createTask + reload
 */
export function openStandup({ people = [], meId = null, projects = {}, rawProjects = [], backOn = {}, tasks = () => [], onCreate }) {
  if (!people.length) return null;
  const el = document.createElement('div');
  el.className = 'tk4-standup';
  el.setAttribute('role', 'dialog');
  el.setAttribute('aria-modal', 'true');
  el.setAttribute('aria-label', 'Standup');
  document.body.appendChild(el);
  // Start on yourself if you are on the team, else the first person.
  let idx = Math.max(0, people.findIndex((p) => p.userId === meId));
  let busy = false;

  const draw = () => {
    el.innerHTML = `<div class="tk4-su-in">${standupHtml({ people, idx, tasks: tasks(), projects, backOn })}</div>`;
  };
  const go = (i) => { idx = Math.min(Math.max(i, 0), people.length - 1); draw(); };
  const close = () => { el.remove(); document.removeEventListener('keydown', onKey, true); };
  const onKey = (ev) => {
    if (!el.isConnected) return;
    if (ev.key === 'Escape') { ev.preventDefault(); ev.stopPropagation(); close(); return; }
    const typing = ev.target && /input|textarea|select/i.test(ev.target.tagName);
    if (typing) return;
    if (ev.key === 'ArrowRight') { ev.preventDefault(); go(idx + 1); }
    if (ev.key === 'ArrowLeft') { ev.preventDefault(); go(idx - 1); }
  };
  document.addEventListener('keydown', onKey, true);

  el.addEventListener('click', (ev) => {
    const who = ev.target.closest('[data-su-person]');
    if (who) return go(Number(who.dataset.suPerson));
    if (ev.target.closest('[data-su-prev]')) return go(idx - 1);
    if (ev.target.closest('[data-su-next]')) return go(idx + 1);
    if (ev.target.closest('[data-su-close]')) return close();
    return undefined;
  });
  el.addEventListener('input', (ev) => {
    const add = ev.target.closest('[data-su-add]');
    if (!add) return;
    const h = el.querySelector('.tk4-add-hint');
    if (h) h.textContent = add.value.trim() ? addHint(parseAdd(add.value, rawProjects)) : '';
  });
  el.addEventListener('keydown', async (ev) => {
    const add = ev.target.closest('[data-su-add]');
    if (!add || ev.key !== 'Enter' || busy) return;
    ev.preventDefault();
    const parsed = parseAdd(add.value, rawProjects);
    if (!parsed) return;
    const person = people[idx];
    busy = true;
    add.disabled = true;
    try {
      await onCreate({ title: parsed.title, projectId: parsed.project ? parsed.project.id : null,
        assigneeId: person.userId, assigneeEmail: person.email });
      draw();
      el.querySelector('[data-su-add]')?.focus();
    } catch (err) {
      add.disabled = false;
      const p = el.querySelector('.tk4-su-err');
      if (p) { p.hidden = false; p.textContent = `The task did not save — ${(err && err.message) || err}`; }
    } finally { busy = false; }
  });

  draw();
  el.querySelector('[data-su-add]')?.focus();
  return { close };
}
